import { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "store/hooks";
import { push } from "connected-react-router";
import logger from "logging/logger";
import {
  selectUser,
  selectMagicLinkEmail,
  selectInviteeEmail,
} from "modules/auth/redux/selectors";
import useRegisterToEvent from "modules/event/hooks/useRegisterToEvent";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import PerformanceLogger from "helpers/performanceHelper";
import { useI18n } from "i18n";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { IEvent } from "modules/event/types";
import { IEventMember } from "modules/eventMemberList/types";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { hasAutoJoin } from "modules/queryParams/queryParamsHelper";
import { useSelector } from "react-redux";
import { useHasAnsweredQuestions } from "modules/registration";
import {
  selectHasPayableTicket,
  selectHasTickets,
  selectIsLoading,
} from "modules/ticketing/redux/selectors";
import { usePrevious } from "helpers/reactHooksUtils";
import { checkEmailInvited } from "modules/eventMemberList/eventMember.api";
import { setShowTickets } from "modules/ticketing/redux/slice";
import { addErrorNotification } from "modules/notification/redux/notificationSlice";
import { isUsersNameMissing } from "modules/auth/components/MissingFullNameDialog";
import {
  hidePrefilledPreferredLoginMethods,
  hideUserPreferredLoginBypass,
} from "modules/auth/redux/authSlice";
import AuthToRegisterToEvent from "./AuthToRegisterToEvent";
import { setShowRegisterDialog } from "../redux/eventLandingSlice";
import RegistrationSuccess from "./RegistrationSuccess";
import withDialog from "./withDialog";

const PageLoadingWithDialog = withDialog(PageLoader);
const AuthToRegisterToEventWithDialog = withDialog(AuthToRegisterToEvent);
const RegistrationSuccessWithDialog = withDialog(RegistrationSuccess);

interface Props {
  event: IEvent;
  onAddToCalendar: () => void;
}

/**
 * This component handles user registering into event
 * If logged in user, register them to event
 * If non logged in user, login/signup and then register
 * If upcoming event, show calendar widget after register
 * If ongoing event, take him into space
 */
const RegisterToEvent = ({
  event,
  onAddToCalendar,
}: Props): JSX.Element | null => {
  const { t } = useI18n(["eventLanding", "event"]);
  const prefilledMagicLinkEmail = useSelector(selectMagicLinkEmail);
  const user = useSelector(selectUser);
  const { checkIfEventHappeningNow, checkIfEventIsUpcoming } =
    useCheckEventStatus();
  const { registerToEvent, isRegistered } = useRegisterToEvent();
  const inviteeEmail = useSelector(selectInviteeEmail);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isInAuth, setIsInAuth] = useState(false);
  const [queuedRegistration, setQueuedRegistration] = useState(false);
  const [eventMember, setEventMember] = useState<IEventMember | undefined>(
    undefined,
  );
  const params = useQueryParams();
  const hasTickets = useSelector(selectHasTickets);
  const isAutoJoin = hasAutoJoin(params);
  const hasPayableTicket = useSelector(selectHasPayableTicket);
  const [isInvitedToPrivateEvent, setIsInvitedToPrivateEvent] = useState(false);
  const isHappeningNow = checkIfEventHappeningNow(event);
  const isLoadingTicketInfo = useSelector(selectIsLoading);
  const isUpcomingEvent = checkIfEventIsUpcoming(event);
  const dispatch = useAppDispatch();
  const {
    hasAnsweredQuestions,
    hasQuestions,
    showRegistrationForm,
    status: questionStatus,
  } = useHasAnsweredQuestions(event?.id);
  const hasPrevAnsweredQuestions = usePrevious(hasAnsweredQuestions);

  const onCloseHandler = () => {
    dispatch(setShowRegisterDialog(false));
    if (inviteeEmail) {
      dispatch(hidePrefilledPreferredLoginMethods());
    } else {
      dispatch(hideUserPreferredLoginBypass());
    }
  };

  const checkAndRegisterUser = useCallback(async () => {
    /** If logged in, register the user into event */
    if (user && event.id) {
      if (event.isPrivate) {
        setIsRegistering(true);
        const { isSuccess, data } = await checkEmailInvited(
          event.id,
          user.email,
        );
        setIsRegistering(false);
        setIsInvitedToPrivateEvent(data.isPrivate && data.invited);
        if (isSuccess && !data.invited && data.isPrivate) {
          if (!isLoadingTicketInfo) {
            dispatch(
              addErrorNotification({
                message: t("you.are.not.invited"),
                position: "tc",
              }),
            );
          }
          return;
        }
      }

      if (isLoadingTicketInfo) return;

      const amIRegistered = event.id
        ? isRegistered(event.id, eventMember ?? null)
        : false;

      if (!amIRegistered && hasPayableTicket) {
        dispatch(setShowTickets(true));
        return;
      }

      if (questionStatus === "success") {
        if (
          hasQuestions &&
          !hasAnsweredQuestions &&
          !event.managers?.includes(user.id)
        ) {
          showRegistrationForm();
        } else {
          setIsRegistering(true);

          const registeredMember = await registerToEvent(event.id);
          if (!registeredMember) {
            setIsRegistering(false);
            dispatch(setShowRegisterDialog(false));
            return;
          }

          setEventMember(registeredMember);

          setIsRegistering(false);
        }
      }
    } else {
      setIsInAuth(true);
    }
  }, [
    user,
    event.id,
    event.isPrivate,
    event.managers,
    isLoadingTicketInfo,
    isRegistered,
    eventMember,
    hasPayableTicket,
    questionStatus,
    dispatch,
    t,
    hasQuestions,
    hasAnsweredQuestions,
    showRegistrationForm,
    registerToEvent,
  ]);

  useEffect(() => {
    if (isLoadingTicketInfo) return;
    if (!user || isUsersNameMissing(user)) return;

    setQueuedRegistration(true);
  }, [isLoadingTicketInfo, user]);

  useEffect(() => {
    if (queuedRegistration && questionStatus === "success") {
      checkAndRegisterUser();
      setQueuedRegistration(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedRegistration, questionStatus]);

  useEffect(() => {
    if (hasAnsweredQuestions && !hasPrevAnsweredQuestions) {
      setQueuedRegistration(true);
    }
  }, [hasAnsweredQuestions, hasPrevAnsweredQuestions]);

  useEffect(() => {
    if (!isHappeningNow || isInAuth || !user || isRegistering || !eventMember) {
      return;
    }

    PerformanceLogger.start("EventLoad");
    dispatch(setShowRegisterDialog(false));
    dispatch(push(`/e/${event.code}${isAutoJoin ? "?autojoin=1" : ""}`));
  }, [
    isHappeningNow,
    isInAuth,
    event,
    user,
    isAutoJoin,
    isRegistering,
    dispatch,
    eventMember,
  ]);

  /**
   * If auth is completed and user object is retrieved, show register success page and then add to calendar
   */
  const onAuthComplete = () => {
    logger.info("[RegisterToEvent][onAuthComplete] Registering to event.");
    setIsInAuth(false);
    setQueuedRegistration(true);
  };

  const getLoggedInUserContent = () => {
    if (isRegistering || isLoadingTicketInfo) {
      return (
        <PageLoadingWithDialog
          message={t<string>("eventLanding:registering")}
        />
      );
    }

    if (
      isUpcomingEvent &&
      questionStatus === "success" &&
      (!hasQuestions || hasAnsweredQuestions) &&
      (!hasTickets || !hasPayableTicket) &&
      ((event.isPrivate && isInvitedToPrivateEvent) || !event.isPrivate)
    ) {
      return (
        <RegistrationSuccessWithDialog
          event={event}
          onAddToCalendar={onAddToCalendar}
        />
      );
    }

    return null;
  };

  return !isInAuth && user ? (
    getLoggedInUserContent()
  ) : (
    <AuthToRegisterToEventWithDialog
      event={event}
      onComplete={onAuthComplete}
      onClose={onCloseHandler}
      initialEmail={prefilledMagicLinkEmail}
    />
  );
};

export default RegisterToEvent;
